import {
	lazy,
	memo,
	Suspense,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { Link } from "react-router-dom";

const ReCAPTCHA = lazy(() => import("react-google-recaptcha"));

const DEFAULT_FOOTER_DATA = {
	Prom: { years: [] },
	Evening: { years: [] },
	Bridal: { years: [] },
};

let footerDataCache = null;

function getCategorySlug(categoryName = "") {
	const category = String(categoryName).trim().toLowerCase();
	if (category.includes("prom")) return "prom";
	if (category.includes("evening")) return "evening";
	if (category.includes("bridal")) return "bridal";
	return category
		.replace(/\s+/g, "-")
		.replace(/[^a-z0-9-]/g, "")
		.replace(/-+/g, "-");
}

const ChevronUpIcon = () => (
	<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
		<path
			d="m6 15 6-6 6 6"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const ChevronDownIcon = () => (
	<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
		<path
			d="m6 9 6 6 6-6"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const MailIcon = () => (
	<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
		<path
			d="M4 6h16v12H4zM4 7l8 6 8-6"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

function Footer() {
	const footerRef = useRef(null);
	const fetchedRef = useRef(Boolean(footerDataCache));

	const [openMenu, setOpenMenu] = useState(null);
	const [footerData, setFooterData] = useState(
		footerDataCache || DEFAULT_FOOTER_DATA
	);
	const [email, setEmail] = useState("");
	const [emailError, setEmailError] = useState("");
	const [subscribeMessage, setSubscribeMessage] = useState(null);
	const [capVal, setCapVal] = useState(null);
	const [status, setStatus] = useState(null);
	const [showRecaptcha, setShowRecaptcha] = useState(false);

	useEffect(() => {
		const target = footerRef.current;
		if (!target || footerDataCache) return undefined;

		let controller = null;
		let timeoutId = null;

		const fetchFooterData = async () => {
			if (fetchedRef.current) return;
			fetchedRef.current = true;

			controller = new AbortController();
			timeoutId = window.setTimeout(() => controller?.abort(), 6000);

			try {
				const response = await fetch(
					"https://admin.monsinidress.com/api/navbar",
					{
						method: "POST",
						headers: { "Content-Type": "application/json" },
						body: JSON.stringify({ brand: "Monsini" }),
						signal: controller.signal,
					}
				);

				if (!response.ok) {
					throw new Error(`Footer request failed: ${response.status}`);
				}

				const result = await response.json();
				const rawData = result?.data || {};
				const formattedData = {};

				Object.entries(rawData).forEach(([category, categoryData]) => {
					const years = Array.isArray(categoryData?.years)
						? [...new Set(categoryData.years)].sort(
								(a, b) => Number(b) - Number(a)
						  )
						: [];
					formattedData[category] = { years };
				});

				const finalData = {
					...DEFAULT_FOOTER_DATA,
					...formattedData,
				};

				footerDataCache = finalData;
				setFooterData(finalData);
			} catch (error) {
				if (error.name !== "AbortError") {
					setFooterData(DEFAULT_FOOTER_DATA);
				}
			} finally {
				if (timeoutId) window.clearTimeout(timeoutId);
			}
		};

		if (!("IntersectionObserver" in window)) {
			const fallbackTimer = window.setTimeout(fetchFooterData, 9000);
			return () => {
				window.clearTimeout(fallbackTimer);
				controller?.abort();
				if (timeoutId) window.clearTimeout(timeoutId);
			};
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (entries[0]?.isIntersecting) {
					fetchFooterData();
					observer.disconnect();
				}
			},
			{
				rootMargin: "500px 0px",
				threshold: 0.01,
			}
		);

		observer.observe(target);

		return () => {
			observer.disconnect();
			controller?.abort();
			if (timeoutId) window.clearTimeout(timeoutId);
		};
	}, []);

	const toggleMenu = useCallback((menuName) => {
		setOpenMenu((current) => (current === menuName ? null : menuName));
	}, []);

	const closeMenu = useCallback(() => setOpenMenu(null), []);

	const getDropdownClasses = useCallback(
		(menuName) => {
			const base =
				"ml-4 mt-2 flex flex-col gap-1 overflow-hidden transition-all duration-300 ease-in-out";
			return `${base} ${
				openMenu === menuName
					? "max-h-60 opacity-100"
					: "max-h-0 opacity-0"
			}`;
		},
		[openMenu]
	);

	const categoryEntries = useMemo(
		() => Object.entries(footerData),
		[footerData]
	);

	const handleSubscribe = useCallback(async () => {
		const cleanEmail = email.trim().toLowerCase();
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

		setEmailError("");
		setSubscribeMessage(null);
		setStatus(null);

		if (!cleanEmail) {
			setEmailError("Email is required");
			return;
		}

		if (!emailRegex.test(cleanEmail)) {
			setEmailError("Please enter a valid email address");
			return;
		}

		if (!showRecaptcha) {
			setShowRecaptcha(true);
			return;
		}

		if (!capVal) {
			setStatus("recaptcha-error");
			return;
		}

		const controller = new AbortController();
		const timeout = window.setTimeout(() => controller.abort(), 7000);

		try {
			const response = await fetch(
				"https://admin.monsinidress.com/api/subscriber",
				{
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						brandName: "Monsini",
						email: cleanEmail,
					}),
					signal: controller.signal,
				}
			);

			if (!response.ok) {
				const errorData = await response.json().catch(() => ({}));
				throw new Error(
					errorData?.message || "Subscription failed. Please try again."
				);
			}

			setSubscribeMessage("Thank you for subscribing! Check your inbox.");
			setEmail("");
			setCapVal(null);
			setShowRecaptcha(false);
			setStatus(null);
			window.setTimeout(() => setSubscribeMessage(null), 4000);
		} catch (error) {
			if (error.name === "AbortError") {
				setSubscribeMessage("The request timed out. Please try again.");
			} else {
				setSubscribeMessage(
					error.message || "Subscription failed. Please try again."
				);
			}
			window.setTimeout(() => setSubscribeMessage(null), 5000);
		} finally {
			window.clearTimeout(timeout);
		}
	}, [email, showRecaptcha, capVal]);

	return (
		<>
			{subscribeMessage && (
				<div
					className={`fixed bottom-4 right-4 z-[1202] flex items-center gap-2 rounded-lg p-3 text-white shadow-xl ${
						subscribeMessage.toLowerCase().includes("failed") ||
						subscribeMessage.toLowerCase().includes("timed out")
							? "bg-red-700"
							: "bg-green-700"
					}`}
					role="status"
					aria-live="polite"
				>
					<MailIcon />
					{subscribeMessage}
				</div>
			)}

			<footer
				ref={footerRef}
				className="w-full overflow-x-hidden bg-pink-700 text-white"
			>
				<div className="grid w-full gap-5 border-b border-white/50 px-6 md:grid-cols-2 md:px-10 lg:grid-cols-4 lg:px-16">
					<div className="flex flex-col items-start justify-center md:px-4 md:py-2 lg:px-10 lg:py-6">
						<h2 className="my-4 font-lora text-3xl">Let's get in Touch</h2>
						<p className="font-poppins text-[14px]">
							Sign up for our newsletter and get news about latest products and
							discounts.
						</p>

						<label htmlFor="footer-email" className="sr-only">
							Email address
						</label>
						<input
							id="footer-email"
							name="email"
							type="email"
							autoComplete="email"
							value={email}
							placeholder="Enter Your Email Address..."
							aria-describedby={
								emailError ? "footer-email-error" : undefined
							}
							aria-invalid={Boolean(emailError)}
							onChange={(event) => setEmail(event.target.value)}
							className="my-2 w-full rounded-lg border-2 border-white bg-transparent px-3 py-2 font-poppins text-[16px] text-white outline-none placeholder:text-white hover:bg-white/10 focus:bg-white/10"
						/>

						{emailError && (
							<p
								id="footer-email-error"
								role="alert"
								className="mt-1 rounded bg-red-800 p-1 text-sm text-white"
							>
								{emailError}
							</p>
						)}

						{showRecaptcha && (
							<div className="mt-2">
								<Suspense fallback={<div className="text-sm">Loading verification...</div>}>
									<ReCAPTCHA
										sitekey="6LeKeywsAAAAAHhTtElwWALyKKJoy9uXHWODEk4f"
										onChange={(value) => setCapVal(value)}
									/>
								</Suspense>

								{status === "recaptcha-error" && (
									<p
										role="alert"
										className="mt-2 rounded bg-red-800 p-2 text-sm font-medium text-white"
									>
										Please complete the reCAPTCHA verification.
									</p>
								)}
							</div>
						)}

						<button
							type="button"
							onClick={handleSubscribe}
							className="mt-4 w-full rounded bg-white px-4 py-2 font-semibold text-pink-800 transition hover:bg-gray-100"
						>
							{showRecaptcha ? "Complete Subscription" : "Subscribe"}
						</button>
					</div>

					<div className="flex flex-col gap-2 py-6 font-Jost-Regular md:px-5 md:py-10 lg:px-5 lg:py-14">
						<h2 className="mb-2 font-lora text-[20px] font-medium">Collections</h2>

						{categoryEntries.map(([categoryName, categoryData]) => {
							const categorySlug = getCategorySlug(categoryName);
							const menuKey = categorySlug;
							const yearsList = categoryData?.years || [];
							const isOpen = openMenu === menuKey;
							const dropdownId = `footer-${menuKey}`;

							return (
								<div key={categoryName} className="font-poppins">
									<button
										type="button"
										onClick={() => toggleMenu(menuKey)}
										aria-expanded={isOpen}
										aria-controls={dropdownId}
										className="flex w-full items-center justify-between rounded px-2 py-1 transition hover:bg-white/10"
									>
										<span>{categoryName}</span>
										<span aria-hidden="true">
											{isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
										</span>
									</button>

									<div id={dropdownId} className={getDropdownClasses(menuKey)}>
										{yearsList.map((year) => (
											<Link
												key={year}
												to={`/${categorySlug}/${year}`}
												aria-label={`${year} ${categoryName} Collection`}
												onClick={closeMenu}
												className="block rounded px-2 py-1 transition hover:bg-white/10 hover:px-3"
											>
												{year} Collection
											</Link>
										))}

										<Link
											to={`/size-chart/${categorySlug}`}
											aria-label={`${categoryName} Size Chart`}
											onClick={closeMenu}
											className="block w-full rounded px-2 py-1 text-left transition hover:bg-white/10 hover:px-3"
										>
											Size Chart
										</Link>
									</div>
								</div>
							);
						})}
					</div>

					<div className="flex flex-col gap-2 py-6 font-Jost-Regular md:px-5 md:py-10 lg:px-5 lg:py-14">
						<h2 className="mb-2 font-lora text-[20px] font-medium">Contact Links</h2>
						<Link
							to="/about"
							className="block rounded px-2 py-1 font-poppins transition hover:bg-white/10"
						>
							About Us
						</Link>
						<Link
							to="/contact"
							className="block rounded px-2 py-1 font-poppins transition hover:bg-white/10"
						>
							Contact Us
						</Link>
						<p className="mt-3">
							<a
								href="https://market.monsiniprom.com/"
								target="_blank"
								rel="noopener noreferrer"
								className="inline-block rounded-md border border-white px-4 py-1.5 font-bold text-white transition hover:bg-white/10"
							>
								Retailer Login
							</a>
						</p>
					</div>

					<div className="flex flex-col gap-2 py-6 md:px-5 md:py-10 lg:px-5 lg:py-14">
						<h2 className="mb-2 font-lora text-[20px] font-medium">Social Links</h2>
						<a
							href="https://www.instagram.com/monsiniprom?igsh=ZnRva3FtZ3czbGU5"
							target="_blank"
							rel="noopener noreferrer"
							className="rounded px-2 py-1 font-poppins transition hover:bg-white/10"
						>
							Instagram
						</a>
					</div>
				</div>
			</footer>
		</>
	);
}

export default memo(Footer);
